import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { brandText } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestSummaryReportInterFace,
  SummaryReportInterFace,
} from '../../../../interface/summary-reports-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { fetchSummaryReport, setSortData } from '../../../../redux/slices/SummaryReportSlice';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../utils/api/location-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}

/**
 * ReportsSummary
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function ReportsSummary(properties: Readonly<ReportLeftTopContent>) {
  const { t } = useTranslation('sidePanel');
  const { leftTopContent } = properties;
  const currentDate = new Date();
  const yesterday = new Date(currentDate);
  yesterday.setDate(currentDate.getDate() - 1);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const { startDate, endDate } = dateRange;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { SummaryReport, limit, page, totalPages, totalResults, loading, sortBy, sortField } =
    useAppSelector((state) => state.SummaryReports);
  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: RequestSummaryReportInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(
        fetchSummaryReport({
          ...data,
          chargingStation: selectedChargingPoint?.id,
          startDate,
          endDate,
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({ startDate, endDate, page, limit, sortBy, sortField });
  };

  /**
   * Function to auto refresh the data
   */
  const handleAutoRefresh = () => {
    fetchData({ startDate, endDate, page, limit, sortBy, sortField });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: SummaryReport,
    });

  const column = [
    {
      label: t('devicesName'),
      accessorKey: 'chargingPointName' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('deviceSnShort'),
      accessorKey: 'chargingPointSn' as keyof SummaryReportInterFace,
      sortAllow: true,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('totalUptime'),
      accessorKey: 'totalUptimeMin' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * Rendering total uptime in time duration
       * @param {SummaryReportInterFace} currentRow - passing as props
       * @returns returning react element
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return <span>{FormatDuration(currentRow.totalUptimeMin, { formatBy: 'min' })}</span>;
      },
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return (
          <p>
            {ToFixedValue(currentRow?.totalRevenue)} {brandText.currency}
          </p>
        );
      },
    },
    {
      label: t('totalEnergyDispensed'),
      accessorKey: 'totalPowerConsumed' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return <p>{ToFixedValue(currentRow?.totalPowerConsumed, 3)} kW</p>;
      },
    },
    {
      label: t('avgChargingSessionsPerDay'),
      accessorKey: 'averageTransactionDurationInMinutes' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return (
          <p>
            {FormatDuration(currentRow?.averageTransactionDurationInMinutes, { formatBy: 'min' })}
          </p>
        );
      },
    },
    {
      label: t('avgSessionDuration'),
      accessorKey: 'totalSessions' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('averageDurationSession'),
      accessorKey: 'averageTransactionDurationSeconds' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return (
          <p>
            {FormatDuration(currentRow?.averageTransactionDurationSeconds, { formatBy: 'sec' })}
          </p>
        );
      },
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('devicesName'), accessorKey: 'chargingPointName' },
    { label: t('deviceSnShort'), accessorKey: 'chargingPointSn' },
    { label: t('stationLocation'), accessorKey: 'chargingPointLocation' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('totalEnergyDispensed'), accessorKey: 'totalPowerConsumed' },
    { label: t('avgChargingSessionsPerDay'), accessorKey: 'totalSessions' },
    { label: t('avgSessionDuration'), accessorKey: 'averageTransactionDurationInMinutes' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    stationManagementApi
      .fetchReportSummaryExport({
        startDate,
        endDate,
        submissionStatus: 'approved',
        sortBy,
        sortField,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: SummaryReportInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || t('unavailable');
              default:
                return (row as any)[col?.accessorKey] || t('unavailable');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-location-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-location-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    fetchData({
      ...value,
      startDate,
      endDate,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    dispatch(setSortData(sortData));
    fetchData({
      startDate,
      endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    fetchData({
      page: DefaultApiLimit.page,
      limit,
      startDate,
      endDate,
    });
  }, [selectedChargingPoint?.id, startDate, endDate]);
  return (
    <div>
      {' '}
      <CustomTable
        version="v2"
        header={column}
        data={SummaryReport}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        leftTopContent={leftTopContent}
        pageLimitChange={pageNateLimitChange}
        sortData={{
          sortBy,
          sortField,
        }}
        height={400}
        handleSortChange={handleSortFunction}
        disableHeader={false}
        rightTopContent={
          <div className="reports-date-picker">
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              defaultPreset="last14Days"
            />
          </div>
        }
        onHiddenColumnsChange={setHiddenColumns}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
      />
    </div>
  );
}

export default ReportsSummary;
